import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ChipComponent } from '../../../../common-shared-module/components/chip/chip.component';
import { HelpLabelComponent } from '../../../../common-shared-module/components/help-label/help-label.component';
import { SingleFieldComponent } from '../../../../common-shared-module/components/single-field/single-field.component';
import { ToolHelpComponent } from '../../../../common-shared-module/components/tool-help/tool-help.component';
import { ItemTypeMetadata, itemTypeMetadataToIcon, itemTypeMetadataToName, ToItemTypeMetadata } from '../../../../common-shared-module/model/dyn-form/form-metadata/item-type-metadata.enum';
import { DataSourceItemMetadata, DataSourceItemMetadataBase } from '../../../../common-shared-module/model/dyn-form/form-metadata/item/datasource-item-metadata.model';
import { DataSourceOptionMetadata, DataSourceOptionMetadataBase } from '../../../../common-shared-module/model/dyn-form/form-metadata/item/datasource-option-item-metadata.model';
import { ItemMetadata } from '../../../../common-shared-module/model/dyn-form/form-metadata/item/item-metadata.model';
import { LengthItemMetadata, LengthItemMetadataBase } from '../../../../common-shared-module/model/dyn-form/form-metadata/item/length-item-metadata.model';
import { ValueItemMetadata, ValueItemMetadataBase } from '../../../../common-shared-module/model/dyn-form/form-metadata/item/value-item-metadata.model';
import { HasShowHideLogic } from '../../../../common-shared-module/model/dyn-form/form-metadata/logic/has-show-hide-logic.model';
import { ShowHideLogicMetadata, ShowHideLogicMetadataBase } from '../../../../common-shared-module/model/dyn-form/form-metadata/logic/show-hide-logic-metadata.model';
import { MetadataChange } from '../../../models/metadata-change.model';
import { sectionEditor } from '../../../models/tool-help.model';
import { SectionMetadataService } from '../../../services/section-metadata/section-metadata.service';
import { ChainedSelectMetadataEditorComponent } from './chained-select-metadata-editor/chained-select-metadata-editor.component';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormlyValidationsEditor } from '../../../../common-shared-module/validation/formly/formly-validation';

@Component({
    selector: 'app-item-metadata-editor',
    templateUrl: './item-metadata-editor.component.html',
    styleUrl: './item-metadata-editor.component.scss',
    standalone: true,
    imports: [
        CommonModule, FormsModule, DragDropModule,
        MatIconModule, MatDividerModule, MatAccordion, MatExpansionModule, MatSlideToggleModule, MatCheckboxModule, MatRadioModule, MatButtonModule,

        ToolHelpComponent, ChipComponent, HelpLabelComponent, ChainedSelectMetadataEditorComponent, SingleFieldComponent
    ]
})
export class ItemMetadataEditorComponent {

    @Input()
    set anItem(i: ItemMetadata) {
        this.item = i
        this.refresh()
    }

    item!: ItemMetadata

    currentFamily: DataSourceItemMetadata[] = []
    itemHasFamily: boolean = false

    onDD: boolean = false

    @Output()
    changeItem: EventEmitter<MetadataChange> = new EventEmitter()

    @Output()
    goParent: EventEmitter<boolean> = new EventEmitter()


    constructor(protected sectionMetadataService: SectionMetadataService, protected dialog: MatDialog) { }

    ngOnInit(): void {

    }

    startDD($event: CdkDragStart) {
        this.onDD = true
    }

    endDD($event: CdkDragEnd) {
        this.onDD = false
    }

    refresh() {
        this.currentFamily = this.family()
        this.itemHasFamily = this.currentFamily.length > 1
    }


    fireChange() {
        let metadataChange = new MetadataChange()
        metadataChange.pushChanged(this.item)
        this.changeItem.emit(metadataChange)
    }

    onGoParent() {
        this.goParent.emit(true)
    }

    fieldNameValidator = sectionEditor.fieldNameValidator


    icon(): string {
        return itemTypeMetadataToIcon(<ItemTypeMetadata>ToItemTypeMetadata(this.item.type))
    }

    helpText(): string {
        return sectionEditor.ItemTypeMetadataToHelpMsg(<ItemTypeMetadata>ToItemTypeMetadata(this.item.type))
    }

    itemName(): string {
        return itemTypeMetadataToName(<ItemTypeMetadata>ToItemTypeMetadata(this.item.type))
    }

    isSelect(): boolean {
        return ToItemTypeMetadata(this.item.type) == ItemTypeMetadata.Select

    }

    hasLength(): LengthItemMetadata | null {
        if (this.item instanceof LengthItemMetadataBase) {
            return this.item

        }
        return null

    }

    LengthItemMetadata(): LengthItemMetadata {
        return <LengthItemMetadata>this.item
    }

    hasDataSource(): DataSourceItemMetadata | null {
        if (this.item instanceof DataSourceItemMetadataBase) {
            return this.item

        }
        return null
    }

    DataSourceItemMetadata(): DataSourceItemMetadata {
        return <DataSourceItemMetadata>this.item

    }

    isMultipleCheckBox(): boolean {
        if (this.item.type == ItemTypeMetadata.MultiCheckBox) {
            return true
        }
        return false
    }

    addOption(index: number): void {
        let op = new DataSourceOptionMetadataBase()
        this.createDefaultCBOption(op)
        this.DataSourceItemMetadata().options.splice(index, 0, op)
        this.sortDefaultValues()
        this.fireChange()
    }

    removeOption(option: DataSourceOptionMetadataBase) {
        let index = this.DataSourceItemMetadata().options.findIndex(p => p == option)
        this.DataSourceItemMetadata().options.splice(index, 1)
        this.deleteOptionValueCB(option)
        this.sortDefaultValues()
        this.fireChange()
    }

    hasFamily(): boolean {
        this.currentFamily = this.family()
        if (this.currentFamily.length > 1) {

            return true
        }

        return false
    }

    family(): DataSourceItemMetadata[] {
        if (ToItemTypeMetadata(this.item.type) == ItemTypeMetadata.Select) {
            return this.sectionMetadataService.searchFamily(<DataSourceItemMetadata>this.item)
        }
        return []
    }

    hasValue(): ValueItemMetadata | null {
        if (this.item instanceof ValueItemMetadataBase) {
            return this.item

        }
        return null

    }

    ValueItemMetadata(): ValueItemMetadata {
        return <ValueItemMetadata>this.item
    }

    getDefaultValueCB(option: DataSourceOptionMetadata) {
        this.createDefaultCBOption(option);
        return this.item.defaultValue[option.id]

    }

    createDefaultCBOption(option: DataSourceOptionMetadata) {
        if (this.isMultipleCheckBox() == false) {
            return
        }
        if (this.item.defaultValue == null) {
            this.item.defaultValue = {};
        }

        if (this.item.defaultValue[option.id] == null) {
            this.item.defaultValue[option.id] = false;
        }
    }

    changeDefaultValueCB($event: MatCheckboxChange, option: DataSourceOptionMetadata) {

        this.item.defaultValue[option.id] = $event.checked

    }

    deleteOptionValueCB(option: DataSourceOptionMetadata) {
        if (this.isMultipleCheckBox() == false) {
            return
        }
        delete this.item.defaultValue[option.id]

    }

    updateFiledName($event: string) {
        if (this.hasFamily()) {
            for (let index = 1; index < this.currentFamily.length; index++) {
                const element = this.currentFamily[index];
                if (element.parentSelectFieldName == this.item.fieldName) {
                    element.parentSelectFieldName = $event
                    break
                }
            }
        }
        this.item.fieldName = $event
    }

    logicShowHideFieldArray = (): ShowHideLogicMetadata[] => {
        return (<HasShowHideLogic>(<any>this.item)).logicShowHideField
    }

    addLogicField = () => {
        (<HasShowHideLogic>(<any>this.item)).logicShowHideField.push(new ShowHideLogicMetadataBase())
    }

    removeLogicField = (index: number) => {
        (<HasShowHideLogic>(<any>this.item)).logicShowHideField.splice(index, 1)
        if ((<HasShowHideLogic>(<any>this.item)).logicShowHideField.length == 0) {
            this.addLogicField()
        }
    }

    logicShowHideSectionArray = (): ShowHideLogicMetadata[] => {
        return (<HasShowHideLogic>(<any>this.item)).logicShowHideSection
    }

    addLogicSection = () => {
        (<HasShowHideLogic>(<any>this.item)).logicShowHideSection.push(new ShowHideLogicMetadataBase())
    }

    removeLogicSection = (index: number) => {
        (<HasShowHideLogic>(<any>this.item)).logicShowHideSection.splice(index, 1)
        if ((<HasShowHideLogic>(<any>this.item)).logicShowHideSection.length == 0) {
            this.addLogicSection()
        }
    }

    drop(event: CdkDragDrop<DataSourceOptionMetadata[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }
        
        this.sortDefaultValues()
        this.fireChange()
    }

    sortDefaultValues() {

        if (this.item instanceof DataSourceItemMetadataBase && this.item.type == 'MULTI_CHECKBOX_ITEM') {

            let newDefaultValues = new Map()
            this.item.options.forEach((p) => {
                let d = this.item.defaultValue[p.id]
                newDefaultValues.set(p.id, d)
            })
            this.item.defaultValue = newDefaultValues

        }
    }

    getGenericValidations() {
        return FormlyValidationsEditor
    }

}
